import { useQuery } from "@tanstack/react-query";
import { useApi } from "@/lib/api";
import { AdminNav } from "@/components/admin/AdminNav";
import { Stat, StatRow } from "@/components/admin/StatRow";

interface Overview {
  generated_at: number;
  users: { total: number; onboarded: number; new_7d: number };
  items: { pending: number; synthesized: number; failed: number; skipped: number };
  synthesis: { ok_24h: number; error_24h: number; avg_latency_ms_24h: number };
  api_health: {
    status: "healthy" | "degraded" | "unknown";
    error_rate_1h: number | null;
    sample_size_1h: number;
  };
  sources: { total: number; active_count: number; failing: number; last_polled: number | null };
  opportunities: { total: number; expired: number; new_24h: number; dismissed: number };
  circles: { active: number; draft: number; archived: number; pending_members: number };
  saved_items: number;
  activity: { dau_24h: number; dau_7d: number; events_24h: number };
  ingestion: {
    ok_24h: number;
    error_24h: number;
    avg_latency_ms_24h: number;
    avg_items_new_24h: number;
  };
}

export function AdminOverview() {
  const api = useApi();
  const ov = useQuery({
    queryKey: ["admin-overview"],
    queryFn: () => api.get<Overview>("/admin/overview"),
    refetchInterval: 30_000,
  });

  return (
    <div className="px-10 py-10 max-w-6xl">
      <header className="mb-8">
        <p className="label-eyebrow">Operations</p>
        <h1 className="mt-2 text-display-2 font-display">Overview</h1>
        <p className="mt-3 text-whisper text-sm max-w-xl leading-relaxed">
          A quiet read of the platform's operational state. Refreshes every 30 seconds.
        </p>
      </header>

      <AdminNav />

      {ov.isLoading ? (
        <p className="text-sm text-whisper">Reading state…</p>
      ) : ov.isError || !ov.data ? (
        <p className="text-sm text-whisper">Couldn't load overview.</p>
      ) : (
        <OverviewSections data={ov.data} />
      )}
    </div>
  );
}

function OverviewSections({ data }: { data: Overview }) {
  const healthLabel =
    data.api_health.status === "healthy"
      ? "✓ healthy"
      : data.api_health.status === "degraded"
        ? "⚠ degraded"
        : "· unknown";
  const healthDetail =
    data.api_health.error_rate_1h != null
      ? `${(data.api_health.error_rate_1h * 100).toFixed(1)}% errors over ${data.api_health.sample_size_1h} runs (1h)`
      : `insufficient signal (${data.api_health.sample_size_1h} runs in last hour)`;

  return (
    <div className="space-y-8">
      <Section eyebrow="API" title="Health">
        <StatRow>
          <Stat
            label="Status"
            value={healthLabel}
            detail={healthDetail}
            tone={data.api_health.status === "degraded" ? "warn" : "neutral"}
          />
          <Stat
            label="Synthesis latency"
            value={`${data.synthesis.avg_latency_ms_24h}ms`}
            detail="avg over last 24h"
          />
          <Stat
            label="Synth ok / err"
            value={`${data.synthesis.ok_24h} / ${data.synthesis.error_24h}`}
            detail="last 24h"
            tone={data.synthesis.error_24h > 0 ? "warn" : "neutral"}
          />
        </StatRow>
      </Section>

      <Section eyebrow="Ingestion" title="Sources + items">
        <StatRow>
          <Stat
            label="Sources"
            value={`${data.sources.active_count ?? 0} / ${data.sources.total ?? 0}`}
            detail="active / total"
          />
          <Stat
            label="Failing"
            value={data.sources.failing ?? 0}
            tone={(data.sources.failing ?? 0) > 0 ? "warn" : "neutral"}
            detail={(data.sources.failing ?? 0) > 0 ? "sources have last_error set" : "no errors"}
          />
          <Stat
            label="Polls ok / err (24h)"
            value={`${data.ingestion.ok_24h} / ${data.ingestion.error_24h}`}
            tone={data.ingestion.error_24h > 0 ? "warn" : "neutral"}
            detail={`avg ${data.ingestion.avg_latency_ms_24h}ms`}
          />
          <Stat
            label="Items pending"
            value={data.items.pending}
            detail="waiting for synthesis"
          />
          <Stat
            label="Items failed"
            value={data.items.failed}
            tone={data.items.failed > 0 ? "warn" : "neutral"}
            detail="awaiting retry"
          />
          <Stat
            label="Items synthesized"
            value={data.items.synthesized}
          />
        </StatRow>
      </Section>

      <Section eyebrow="Activity" title="Retention signal">
        <StatRow>
          <Stat
            label="DAU (24h)"
            value={data.activity.dau_24h}
            detail="distinct users emitting events"
          />
          <Stat
            label="WAU (7d)"
            value={data.activity.dau_7d}
            detail="distinct users last 7d"
          />
          <Stat
            label="Events (24h)"
            value={data.activity.events_24h}
            detail="see Activity tab for breakdown"
          />
        </StatRow>
      </Section>

      <Section eyebrow="Membership" title="Users + circles">
        <StatRow>
          <Stat
            label="Users"
            value={data.users.total}
            detail={`${data.users.onboarded} onboarded`}
          />
          <Stat
            label="New (7d)"
            value={data.users.new_7d}
          />
          <Stat
            label="Saved items"
            value={data.saved_items}
          />
          <Stat
            label="Circles"
            value={`${data.circles.active} / ${data.circles.active + data.circles.draft + data.circles.archived}`}
            detail="active / total"
          />
          <Stat
            label="Pending requests"
            value={data.circles.pending_members}
            tone={data.circles.pending_members > 0 ? "warn" : "neutral"}
            detail="awaiting circle leader"
          />
        </StatRow>
      </Section>

      <Section eyebrow="Engine" title="Opportunities">
        <StatRow>
          <Stat label="Total" value={data.opportunities.total ?? 0} />
          <Stat
            label="New (24h)"
            value={data.opportunities.new_24h ?? 0}
          />
          <Stat
            label="Expired"
            value={data.opportunities.expired ?? 0}
            detail="past deadline"
          />
          <Stat
            label="Dismissed"
            value={data.opportunities.dismissed ?? 0}
            detail="hidden from feed"
          />
        </StatRow>
      </Section>

      <p className="text-[10px] uppercase tracking-[0.18em] text-muted">
        Snapshot taken {timeAgo(data.generated_at)}
      </p>
    </div>
  );
}

function Section({ eyebrow, title, children }: { eyebrow: string; title: string; children: React.ReactNode }) {
  return (
    <section>
      <header className="mb-3">
        <p className="label-eyebrow">{eyebrow}</p>
        <h2 className="mt-1 font-display text-lg tracking-tight">{title}</h2>
      </header>
      {children}
    </section>
  );
}

function timeAgo(ts: number): string {
  const s = Math.round((Date.now() - ts) / 1000);
  if (s < 60) return `${s}s ago`;
  return `${Math.round(s / 60)}m ago`;
}
